import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offerTitle: string;
  isSubmitting?: boolean;
  /** Kalles KUN med en trimmet, ikke-tom begrunnelse. */
  onConfirm: (reason: string) => void;
}

/**
 * VIB-1304 (Erik 15.07): påkrevd begrunnelse ved statusendring til "Tapt" —
 * fra BÅDE tilbudseditoren og kanban-boardet (samme komponent, samme
 * kodesti via useOfferStatusChange). Avbryt gir INGEN statusendring — kortet/
 * tilbudet forblir i sin nåværende status til dialogen bekreftes.
 *
 * Begrunnelsen lagres i offers.lost_reason og blir analysegrunnlag for
 * VIB-1312 (kalibrering av sannsynlighetsvektene med ekte vinnrater).
 */
export function LostReasonDialog({ open, onOpenChange, offerTitle, isSubmitting, onConfirm }: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length > 0 && !isSubmitting;

  function handleConfirm() {
    if (!canConfirm) return;
    onConfirm(trimmed);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Marker «{offerTitle}» som tapt</DialogTitle>
          <DialogDescription>
            Hvorfor ble tilbudet ikke akseptert? Begrunnelsen er påkrevd og hjelper oss
            forstå hvorfor tilbud tapes over tid.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          <Label htmlFor="lost-reason">Begrunnelse</Label>
          <Textarea
            id="lost-reason"
            placeholder="F.eks. valgte konkurrent, for høy pris, prosjektet utsatt…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Avbryt
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {isSubmitting ? "Lagrer…" : "Marker som tapt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
